import { jsxs, jsx } from "react/jsx-runtime";
import { useRef } from "react";
import classNames from "classnames";
import { ZIndex, AnalyticsId, IconSize } from "./constants.js";
import { useExpand } from "./hooks/useExpand.js";
import { useHover } from "./hooks/useHover.js";
import { useSize } from "./hooks/useSize.js";
import { B as Button } from "./Button.js";
import { I as Icon } from "./Icon.js";
import ChevronDown from "./components/Icons/ChevronDown.js";
import ChevronUp from "./components/Icons/ChevronUp.js";
import { L as LazyIcon } from "./LazyIcon.js";
import styles from "./components/Expander/styles.module.scss";
var ExpanderSize = /* @__PURE__ */ ((ExpanderSize2) => {
  ExpanderSize2["small"] = "small";
  ExpanderSize2["large"] = "large";
  return ExpanderSize2;
})(ExpanderSize || {});
const Expander = (props) => {
  const {
    title,
    children,
    size = "small",
    color,
    contentClassNames,
    svgIcon: icon,
    expanded = false,
    noNestedLine = false,
    sticky = false,
    testId,
    onExpand,
    renderChildrenWhenClosed = false,
    zIndex = ZIndex.ExpanderTrigger
  } = props;
  const [isExpanded, setIsExpanded] = useExpand(expanded, onExpand);
  const expanderRef = useRef(null);
  const triggerRef = useRef(null);
  const { isHovered } = useHover(triggerRef);
  const contentSize = useSize(expanderRef);
  const triggerSize = useSize(triggerRef);
  const isSticky = sticky && isExpanded;
  const renderChevron = (align) => /* @__PURE__ */ jsx("span", { className: classNames(styles["expander__icon"], styles[`expander__icon--${align}`]), children: /* @__PURE__ */ jsx(Icon, { svgIcon: isExpanded ? ChevronUp : ChevronDown, size: IconSize.XSmall, isHovered }) });
  const triggerClassName = classNames(
    styles["expander__trigger"],
    size === "large" && styles[`expander__trigger--${size}`],
    size === "large" && styles[`expander__trigger--${color || "neutral"}`],
    size === "large" && icon && styles["expander__trigger--icon"],
    isExpanded && styles["expander__trigger--expanded"],
    isSticky && styles["expander__trigger--sticky"]
  );
  const renderTrigger = () => /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      className: triggerClassName,
      style: {
        zIndex: isHovered || isSticky ? zIndex : void 0,
        width: isSticky && (contentSize == null ? void 0 : contentSize.width) ? `${contentSize.width}px` : void 0
      },
      "aria-expanded": isExpanded,
      ref: triggerRef,
      onClick: () => setIsExpanded(!isExpanded),
      "data-testid": testId,
      "data-analyticsid": AnalyticsId.Expander,
      children: [
        size === "small" && renderChevron("left"),
        icon && /* @__PURE__ */ jsx("span", { className: classNames(styles["expander__icon"], styles["expander__icon--left"]), children: typeof icon === "string" ? /* @__PURE__ */ jsx(LazyIcon, { iconName: icon, size: IconSize.XSmall, isHovered }) : /* @__PURE__ */ jsx(Icon, { svgIcon: icon, size: IconSize.XSmall, isHovered }) }),
        title,
        size === "large" && renderChevron("right")
      ]
    }
  );
  const buttonClassName = classNames(styles["expander__button"], isExpanded && styles["expander__button--expanded"]);
  const renderButton = () => /* @__PURE__ */ jsx(
    "div",
    {
      style: {
        width: isSticky && (triggerSize == null ? void 0 : triggerSize.width) ? `${triggerSize == null ? void 0 : triggerSize.width}px` : void 0,
        height: isSticky && (triggerSize == null ? void 0 : triggerSize.height) ? `${triggerSize == null ? void 0 : triggerSize.height}px` : void 0
      },
      className: classNames({
        [styles["expander__button-container--sticky"]]: isSticky
      }),
      children: /* @__PURE__ */ jsxs(
        Button,
        {
          variant: "borderless",
          textClassName: styles["expander__button__text"],
          className: buttonClassName,
          "aria-expanded": isExpanded,
          ref: triggerRef,
          onClick: () => setIsExpanded(!isExpanded),
          testId,
          "data-analyticsid": AnalyticsId.Expander,
          children: [
            /* @__PURE__ */ jsx(Icon, { svgIcon: isExpanded ? ChevronUp : ChevronDown, size: IconSize.XSmall }),
            title
          ]
        }
      )
    }
  );
  const renderContent = () => {
    if (!renderChildrenWhenClosed && !isExpanded) {
      return null;
    }
    const contentClassName = classNames(
      styles["expander__content"],
      styles[`expander__content--${size}`],
      size === "large" && styles[`expander__content--${color || "neutral"}`],
      size === "large" && icon && styles["expander__content--icon"],
      isExpanded && styles["expander__content--expanded"],
      size === "small" && !noNestedLine && styles["expander__content--nested-line"],
      { [styles["expander__content--sticky"]]: isSticky },
      contentClassNames
    );
    return /* @__PURE__ */ jsx("div", { className: contentClassName, children });
  };
  return /* @__PURE__ */ jsxs("div", { className: styles["expander"], ref: expanderRef, children: [
    size === "large" ? renderTrigger() : renderButton(),
    renderContent()
  ] });
};
export {
  Expander as E,
  ExpanderSize as a
};
//# sourceMappingURL=Expander.js.map
